import React, { useContext, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import Footer from "./Footer";
import Header from "./Header";
import Sidebar from "./Sidebar";
import Loader from "./Loader";
import BreadCrumb from "./BreadCrumb";
import CompanyFilter from "./CompanyFilter";
import useAuth from "../hooks/useAuth";
import useNotification from "../hooks/useNotification";
import appContext from "../app/contextState/context";
import Notification from "./notificationPopup";
import { Button } from "react-bootstrap";
import { Box, Typography } from "@mui/material";
import { PoliciesContext } from "../hooks/policiesContext";
import ManagementSystemFilter from "./managementSystemFilter";
import api from "../app/api";
import useAlert from "../hooks/useAlert";
import { keys } from "../config/keys";
import StandaloneHeader from "./StandaloneHeader";
import { isStandalonePath, canAccessStandaloneFeature, STANDALONE_FEATURES } from "../utils/standaloneFeatures";

const STANDALONE_ACCESS = {
  "/standalone/companies": { adminOnly: true },
  "/standalone/company-admins": { adminOnly: false },
  "/standalone/fellow-admins": { adminOnly: false },
  "/standalone/manage-teams": { adminOnly: false, employeeAllowed: true },
  "/standalone/manage-sites": { adminOnly: false, employeeAllowed: false },
};

const Screen = ({
  loading,
  children,
  filter,
  setFilter,
  flow,
  showBranchFilter,
  dontShowCompany,
  showShare,
  allowed,
  showAdminFilter,
  showCopyLink,
  faqsId,
  standaloneTitle,
}) => {
  const { pathname } = useLocation();
  const isStandalone = isStandalonePath(pathname);
  const navigate = useNavigate();
  const appState = useContext(appContext);
  const { onClick } = useNotification();
  const { selectedForShare } = useContext(PoliciesContext);
  const { showAlert } = useAlert()
  const {
    showNotification,
    setshowNotification,
    notificationData,
    selectedChecklists,
    addMultipleChecklistsAdd,
    addMultipleRiskAssesmentssAdd,
  } = appState;
  const {
    user,
    isAdmin,
    isGuest,
    isManager,
    isHR,
    isCompany,
    isSupervisor,
    isEmployee,
    isCompanyAdmin,
    isFellowAdmin,
    hasBranchId,
    onUser,
  } = useAuth();
  const location = useLocation()
  const adminOnly = ["/companies", "/categories", "/natures", "/admins"];
  const employeeOnly = isGuest
    ? ["/dashboard", "/records", "/training"]
    : [
      "/dashboard",
      "/training",
      "/task",
      "/checklists",
      "/tasks",
      "/tasks/Daily",
      "/tasks/Weekly",
      "/tasks/Annual",
      "/records",
      "/broadcast-details",
      "/tickets",

          "/pending-mails",

      ...(isSupervisor
        ? []
        : [
          "/documents",
          "/policy",
          "/policyInput",
          "/policies-options",
          "/share",
          "/sentMails",
          "/user-profile"
        ]),
      ...(!isManager
        ? []
        : [
          "/checklists",
          "/new-checklist",
          "/profile",
          "/inbox",
          "/risk-assesments",
          "/new-risk-assesment",
          "/email",
          "/audits",
          "/new-audit",
          "/agreement",
          "/user-profile",
          "/tickets",
          "/form-list",
          "/add-form",
          "/submission-list",
          "/agreement-submissions"
        ]),
    ];
  const standaloneRule = STANDALONE_ACCESS[pathname];
  const isUserHydrated = Boolean(user?._id);
  const standaloneFeatureDenied =
    (pathname === STANDALONE_FEATURES.MANAGE_TEAMS.path &&
      !canAccessStandaloneFeature("MANAGE_TEAMS", {
        isAdmin,
        isCompany,
        isManager,
        isHR,
        branchID:
          new URLSearchParams(location.search).get("branchID") ||
          (hasBranchId ? user?.branchID?._id || user?.branchID : null),
      })) ||
    (pathname === STANDALONE_FEATURES.MANAGE_SITES.path &&
      !canAccessStandaloneFeature("MANAGE_SITES", {
        isAdmin,
        isCompany,
        isManager,
        isHR,
        branchID:
          new URLSearchParams(location.search).get("branchID") ||
          (hasBranchId ? user?.branchID?._id || user?.branchID : null),
      })) ||
    (pathname === STANDALONE_FEATURES.COMPANIES.path &&
      !canAccessStandaloneFeature("COMPANIES", { isAdmin, isCompanyAdmin, isFellowAdmin })) ||
    (pathname === STANDALONE_FEATURES.COMPANY_ADMINS.path &&
      !canAccessStandaloneFeature("COMPANY_ADMINS", { isAdmin, isCompanyAdmin, isFellowAdmin })) ||
    (pathname === STANDALONE_FEATURES.FELLOW_ADMINS.path &&
      !canAccessStandaloneFeature("FELLOW_ADMINS", { isAdmin, isCompanyAdmin, isFellowAdmin }));

  const standaloneNoAccess =
    isStandalone &&
    isUserHydrated &&
    ((standaloneRule &&
      ((!isAdmin && standaloneRule.adminOnly) ||
        (isEmployee && standaloneRule.employeeAllowed === false))) ||
      standaloneFeatureDenied);

  const legacyNoAccess =
    (!isAdmin && adminOnly.includes(pathname)) ||
    (isEmployee && !employeeOnly.includes(pathname));

  const noAccess =
    standaloneNoAccess ||
    (!isStandalone && isUserHydrated && legacyNoAccess) ||
    (isUserHydrated && user?.status == false);
  useEffect(() => {
    onUser();
  }, []);

  useEffect(() => {
    if (!isStandalone) return undefined;
    document.body.classList.add("standalone-page");
    return () => document.body.classList.remove("standalone-page");
  }, [isStandalone]);
  const handleShare = async () => {
    // setLoading(true)
    try {
      const response = await api.post("/share/add", {
        allowedEmails: [],
        shareable: selectedForShare,
        forFeature: location.pathname == '/documents' ? "document" : "management system",
        createdBy: localStorage.getItem("_id"),
        isCopyLink: true
      });
      if (response.data?.status) {
        copyFormLink(response.data.result?._id)
      } else {
        showAlert(response.data?.message, "danger");
      }
    } catch (error) {
      showAlert(error?.message, "danger");
    } finally {
      // setLoading(false)
    }
  };
  const copyFormLink = async (id) => {
    try {
      let link = `${keys.CLIENT_ORIGIN}/shareView?id=${id}&allowed=all`
      showAlert("Link copied to clipboard", "success")
      await navigator.clipboard.writeText(link);
    } catch (error) {
      console.log(error.message)
    } finally {
      // setLoading(false)
    }
  }
  return noAccess ? (
    <Navigate to={"*"} />
  ) : (
    <div style={{ position: "relative" }}>
      {isStandalone ? (
        <StandaloneHeader
          title={
            standaloneTitle ||
            Object.values(STANDALONE_FEATURES).find((feature) => feature.path === pathname)?.title
          }
        />
      ) : (
        <Header />
      )}
      {!isStandalone ? <Sidebar /> : null}
      <Notification
        showNotification={showNotification}
        setshowNotification={setshowNotification}
        notificationData={notificationData}
        onClick={() => {
          onClick(notificationData);
          setshowNotification(false);
        }}
      />
      {allowed && (
        <Box
          sx={{
            height: "100%",
            width: "100%",
            position: "absolute",
            backgroundColor: "rgba(0,0,0,0.2)",
            zIndex: "99",
            backdropFilter: "blur(5px)",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
            }}
          >
            <Typography sx={{ fontSize: "24px", color: "black" }}>
              Your subscription does not include this feature.
            </Typography>
          </Box>
        </Box>
      )}
      <main
        id="main"
        style={{
          minHeight: isStandalone ? "calc(100vh - 64px)" : "85vh",
          marginLeft: isStandalone ? 0 : undefined,
        }}
        className={isStandalone ? "main standalone-main" : "main"}
      >
        <div className="d-flex w-100 justify-content-between align-items-center">
          {isStandalone ? null : <BreadCrumb flow={flow} faqsId={faqsId} />}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row-reverse",
            }}
          >
            {filter ? (
              <CompanyFilter
                dontShowCompany={dontShowCompany}
                filter={filter}
                setFilter={setFilter}
                showBranch={showBranchFilter}
                showAdminFilter={showAdminFilter}
              />
            ) : null}

            {dontShowCompany && (
              <Button
                style={{
                  marginRight: "20px",
                }}
                disabled={selectedChecklists.length == 0}
                onClick={
                  pathname != "/risk-assesments"
                    ? addMultipleChecklistsAdd
                    : addMultipleRiskAssesmentssAdd
                }
              >
                Add
              </Button>
            )}
            {showShare && (
              <Button
                style={{
                  marginRight: "20px",
                }}
                disabled={selectedForShare.length == 0}
                onClick={() => {
                  navigate("/share", {
                    state: {
                      selectedForShare,
                      isDocument: flow[0]?.to?.includes("documents"),
                      isManagementSystem: flow[0]?.to?.includes("management-system"),
                      isForm: flow[0]?.to?.includes("form-list"),
                      isAgreement: flow[0]?.to?.includes("agreement"),
                    },
                  });
                }}
              >
                Share
              </Button>
            )}
            {showCopyLink && (
              <Button
                style={{
                  marginRight: "20px",
                }}
                disabled={selectedForShare.length == 0}
                onClick={() => {
                  handleShare()
                }}
              >
                Create Shareable Link
              </Button>
            )}
          </Box>
        </div>
        <Loader loading={loading} center>
          {children}
        </Loader>
      </main>
      {isStandalone ? null : <Footer />}
    </div>
  );
};

export default Screen;
